import { useState } from 'react';
import { Alert, Box, Button, Collapse, Divider, Paper, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import {
  ALREADY_COMPLETE_DETAIL,
  alreadyCompleteLabel,
  exclusionCopy,
  messageCount,
  patientCount,
} from '../bulkVocabulary';
import type { SubjectLanguage } from '../types';

export interface ExclusionBreakdownEntry {
  reason: string;
  count: number;
}

export interface ExcludedPatientView {
  patientId: string;
  patientName?: string;
  reason: string;
}

export interface RenderedMessagePreview {
  smsBody?: string;
  emailSubject?: string;
  emailBody?: string;
}

export interface BulkLaunchPreviewProps {
  requirementLabel: string;
  /**
   * The definition's kind, used only to name the already-complete stat in the
   * requirement's own noun ("Already have a card"). Optional: an unknown kind
   * falls back to a neutral phrase rather than guessing a noun from the label.
   */
  requirementKind?: string;
  /** Patients who will be messaged. */
  eligiblePatients: number;
  /** Patients who already meet the requirement, so nothing is sent to them. */
  alreadyCompletePatients: number;
  /** Patients excluded from this batch by the eligibility checks. */
  excludedPatients: number;
  /**
   * Messages this launch will send. NOT the patient count — a patient reachable
   * by SMS and email is one patient and two messages.
   */
  messagesToSend: number;
  /**
   * Per-reason counts across the whole excluded population. Undefined when the
   * server cannot break them down; the panel then falls back to naming the
   * patients rather than inventing counts from one page.
   */
  exclusionBreakdown?: ExclusionBreakdownEntry[];
  /**
   * One page of excluded patients — answers "who", not "how many of each".
   *
   * Undefined when the server sent no page, which is not the same as an empty
   * one: empty means nobody, absent means we cannot say who. The panel admits
   * the difference rather than reporting "Showing 0 of 900".
   */
  excludedPatientsPage?: ExcludedPatientView[];
  /**
   * The message as the patient will receive it, secure link included (AC-11).
   * Undefined when it could not be rendered — the launch is withheld, because
   * nobody should bulk-send text they were not shown.
   */
  messagePreview?: RenderedMessagePreview;
  /**
   * False when the preview is a FLOOR rather than the whole population — the
   * population scan hit a guard rail. Every number still adds up, which is
   * exactly why this cannot be inferred: the flag is the only signal, and a
   * launch offered on a floor sends to people nobody reviewed.
   */
  canLaunch?: boolean;
  subject: SubjectLanguage;
  isLaunching?: boolean;
  /**
   * Omitted while cut 2's launch endpoint does not exist. Absent means the
   * launch is WITHHELD, not disabled and not a no-op: it is folded into
   * `canLaunch` below rather than called as `onLaunch?.()`, because an
   * optional call behind a live-looking button is the one shape that turns
   * "cannot send" into "pressed send and nothing happened".
   */
  onLaunch?: () => void;
  onDismiss: () => void;
}

/**
 * The audience preview an admin reads before launching a bulk request (AC-10),
 * with the read-only message (AC-11).
 *
 * Presentational: every number arrives as a prop, so the panel renders the same
 * whether it is fed the live preview endpoint or a fixture.
 */
export const BulkLaunchPreview = ({
  requirementLabel,
  requirementKind,
  eligiblePatients,
  alreadyCompletePatients,
  excludedPatients,
  messagesToSend,
  exclusionBreakdown,
  excludedPatientsPage,
  messagePreview,
  canLaunch: previewIsComplete = true,
  subject,
  isLaunching = false,
  onLaunch,
  onDismiss,
}: BulkLaunchPreviewProps) => {
  const theme = useTheme();
  const [exclusionsOpen, setExclusionsOpen] = useState(false);

  // A subject with no body is not a message to review — it is a header. AC-11
  // asks the admin to read what the patient will read.
  const hasMessage = Boolean(messagePreview?.smsBody || messagePreview?.emailBody);
  const hasAudience = eligiblePatients > 0;
  // Withheld rather than disabled, and for two different reasons that both end
  // in "there is nothing safe to press".
  const canLaunch = hasMessage && hasAudience && previewIsComplete && onLaunch !== undefined;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        Send requests — {requirementLabel}
      </Typography>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.25 }}>
        This goes to eligible active {subject.plural} missing this item. To see who they are first,
        open the {subject.plural} list and filter by Requirements.
      </Typography>

      <Stack direction="row" spacing={4} sx={{ my: 2, flexWrap: 'wrap', rowGap: 1.5 }}>
        <Stat
          value={eligiblePatients}
          label={`${subject.plural} will get a request`}
          tone={theme.palette.text.primary}
        />
        <Stat
          value={alreadyCompletePatients}
          label={alreadyCompleteLabel(requirementKind)}
          tone={theme.palette.text.primary}
          detail={ALREADY_COMPLETE_DETAIL}
        />
        <Stat
          value={excludedPatients}
          label="left out (see why below)"
          tone={theme.palette.warning.main}
        />
      </Stack>

      {excludedPatients > 0 && (
        <>
          <Button
            size="small"
            variant="text"
            onClick={() => setExclusionsOpen(open => !open)}
            sx={{ textTransform: 'none' }}
          >
            {exclusionsOpen ? 'Hide reasons' : 'Why are some left out?'}
          </Button>
          <Collapse in={exclusionsOpen}>
            <Box
              sx={{
                mt: 1,
                pl: 1.5,
                borderLeft: `2px solid ${theme.palette.grey[200]}`,
              }}
            >
              {/* An empty breakdown is not a breakdown: `[]` is truthy, and
                  treating it as one renders an open disclosure with nothing
                  inside it for a population that definitely has reasons. */}
              {exclusionBreakdown?.length ? (
                exclusionBreakdown.map(entry => {
                  const copy = exclusionCopy(entry.reason);
                  return (
                    <Box key={entry.reason} sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {entry.count.toLocaleString()} — {copy.label}
                      </Typography>
                      {copy.detail && (
                        <Typography
                          variant="caption"
                          sx={{ display: 'block', color: theme.palette.text.secondary }}
                        >
                          {copy.detail}
                        </Typography>
                      )}
                    </Box>
                  );
                })
              ) : (
                // No breakdown from the server. Naming the patients on this page
                // is honest; counting this page and calling it the population
                // would not be.
                <>
                  {(excludedPatientsPage ?? []).map(patient => {
                    const copy = exclusionCopy(patient.reason);
                    return (
                      <Typography key={patient.patientId} variant="body2" sx={{ mb: 0.5 }}>
                        {patient.patientName ?? patient.patientId} — {copy.label}
                      </Typography>
                    );
                  })}
                  {excludedPatientsPage === undefined ? (
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                      We could not load who was left out.
                    </Typography>
                  ) : (
                    excludedPatientsPage.length < excludedPatients && (
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                        Showing {excludedPatientsPage.length} of {excludedPatients}.
                      </Typography>
                    )
                  )}
                </>
              )}
            </Box>
          </Collapse>
        </>
      )}

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        Message {subject.plural} will get
      </Typography>

      {hasMessage ? (
        <>
          {/* Every field that unlocks the send is rendered here. Anything that
              counts towards `hasMessage` and is not shown is a bulk send of
              text the admin never read, which is the one outcome AC-11 is for. */}
          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: theme.palette.grey[50] }}>
            {messagePreview?.smsBody && (
              <>
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                  Text message
                </Typography>
                <Typography variant="body2" sx={{ mb: messagePreview.emailBody ? 1.5 : 0 }}>
                  {messagePreview.smsBody}
                </Typography>
              </>
            )}
            {(messagePreview?.emailSubject || messagePreview?.emailBody) && (
              <>
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                  Email{messagePreview.emailSubject ? ` — ${messagePreview.emailSubject}` : ''}
                </Typography>
                {messagePreview.emailBody && (
                  <Typography variant="body2">{messagePreview.emailBody}</Typography>
                )}
              </>
            )}
          </Paper>
          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 0.75, color: theme.palette.text.secondary }}
          >
            To change the wording, edit the default messages below. The secure link is added to
            every message and cannot be removed.
          </Typography>
        </>
      ) : (
        <Alert severity="warning" sx={{ mt: 0.5 }}>
          The message could not be loaded, so there is nothing to review. Sending is unavailable
          until it loads — reload the page to try again.
        </Alert>
      )}

      <Stack direction="row" spacing={1.5} sx={{ mt: 2, alignItems: 'center' }}>
        {canLaunch && (
          <Button
            variant="contained"
            onClick={onLaunch}
            disabled={isLaunching}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {isLaunching
              ? 'Sending…'
              : `Send ${patientCount(eligiblePatients, subject.plural, subject.singular)}`}
          </Button>
        )}
        <Button variant="text" onClick={onDismiss} sx={{ textTransform: 'none' }}>
          {canLaunch ? 'Cancel' : 'Close'}
        </Button>
        {canLaunch && (
          // The two numbers differ whenever anyone is reachable twice, and the
          // progress view counts messages. Saying so here is cheaper than an
          // admin discovering it when 128 becomes 217.
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            {messageCount(messagesToSend)} across SMS and email
          </Typography>
        )}
      </Stack>

      {!previewIsComplete && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          This is a partial count — there are more {subject.plural} than shown, so there is nothing
          complete to review yet. Try again shortly.
        </Alert>
      )}

      {!hasAudience && hasMessage && previewIsComplete && (
        <Typography variant="body2" sx={{ mt: 1, color: theme.palette.text.secondary }}>
          Nobody is eligible for this batch right now, so there is nothing to send.
        </Typography>
      )}
    </Paper>
  );
};

const Stat = ({
  value,
  label,
  tone,
  detail,
}: {
  value: number;
  label: string;
  tone: string;
  detail?: string;
}) => (
  <Box sx={{ minWidth: 92, maxWidth: 200 }}>
    <Typography
      sx={{
        fontSize: 24,
        fontWeight: 700,
        lineHeight: 1.1,
        color: tone,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value.toLocaleString()}
    </Typography>
    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
      {label}
    </Typography>
    {detail && (
      <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled' }}>
        {detail}
      </Typography>
    )}
  </Box>
);
